import type { InputManager } from '../input/InputManager';

const JOYSTICK_RADIUS = 56;
const JOYSTICK_DEADZONE = 0.12;
const LOOK_DRAG_SCALE = 1;
/** Pixels of movement before a press becomes look-drag instead of tap/hold. */
const LOOK_SLOP_PX = 14;
/** Hold still this long (without dragging) to start mining / hold-attack. */
const HOLD_PRIMARY_MS = 140;

function prefersTouchUi(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

export interface TouchControlsOptions {
  /** Direct forge/inventory open — preferred over one-frame input pulse. */
  onForge?: () => void;
  /** Direct shop open when standing in a shop voxel. */
  onShop?: () => void;
  onPause?: () => void;
}

/**
 * On-screen move joystick + tap-anywhere attack / drag-to-look for touch.
 * Tap or hold on empty space to mine/attack; drag to look.
 */
export class TouchControls {
  private readonly root: HTMLDivElement;
  private readonly lookLayer: HTMLDivElement;
  private readonly stickBase: HTMLDivElement;
  private readonly stickKnob: HTMLDivElement;
  private readonly jumpBtn: HTMLButtonElement;
  private readonly pauseBtn: HTMLButtonElement;
  private readonly forgeBtn: HTMLButtonElement;
  private readonly shopBtn: HTMLButtonElement;
  private readonly input: InputManager;
  private readonly parent: HTMLElement;
  private readonly options: TouchControlsOptions;

  private enabled = false;
  private visible = false;
  private shopAvailable = false;
  private movePointerId: number | null = null;
  private lookPointerId: number | null = null;
  private jumpPointerId: number | null = null;
  private stickOriginX = 0;
  private stickOriginY = 0;
  private stickRadius = JOYSTICK_RADIUS;
  private lastLookX = 0;
  private lastLookY = 0;
  private lookStartX = 0;
  private lookStartY = 0;
  private lookDragging = false;
  private primaryHeldFromTouch = false;
  private holdPrimaryTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly onPointerDown: (e: PointerEvent) => void;
  private readonly onPointerMove: (e: PointerEvent) => void;
  private readonly onPointerUp: (e: PointerEvent) => void;
  private readonly onContextMenu: (e: Event) => void;
  private readonly onVisibility: () => void;

  constructor(parent: HTMLElement, input: InputManager, options: TouchControlsOptions = {}) {
    this.parent = parent;
    this.input = input;
    this.options = options;

    this.root = document.createElement('div');
    this.root.id = 'touch-controls';
    this.root.innerHTML = `
      <div class="touch-look" aria-hidden="true"></div>
      <div class="touch-stick" aria-label="Move">
        <div class="touch-stick-knob"></div>
      </div>
      <button type="button" class="touch-btn touch-jump" aria-label="Jump">Jump</button>
      <button type="button" class="touch-btn touch-forge" aria-label="Forge">
        <span class="touch-forge-icon" aria-hidden="true">
          <svg viewBox="0 0 64 64"><path fill="currentColor" d="M12 40h40v8H12zm6-6h28l-4-10H22zm8-14h12l2-6H24z"/><path fill="#ffb347" d="M28 12h8v8h-8z"/></svg>
        </span>
        <span class="touch-forge-label">Forge</span>
      </button>
      <button type="button" class="touch-btn touch-shop" aria-label="Shop" hidden>
        <span class="touch-shop-icon" aria-hidden="true">
          <svg viewBox="0 0 64 64"><circle cx="32" cy="32" r="18" fill="none" stroke="currentColor" stroke-width="4"/><path fill="currentColor" d="M28 20h8v4h-3v16h3v4h-8v-4h3V24h-3z"/></svg>
        </span>
        <span class="touch-shop-label">Shop</span>
      </button>
      <button type="button" class="touch-btn touch-pause" aria-label="Pause">II</button>
    `;
    parent.appendChild(this.root);

    this.lookLayer = this.root.querySelector('.touch-look') as HTMLDivElement;
    this.stickBase = this.root.querySelector('.touch-stick') as HTMLDivElement;
    this.stickKnob = this.root.querySelector('.touch-stick-knob') as HTMLDivElement;
    this.jumpBtn = this.root.querySelector('.touch-jump') as HTMLButtonElement;
    this.forgeBtn = this.root.querySelector('.touch-forge') as HTMLButtonElement;
    this.shopBtn = this.root.querySelector('.touch-shop') as HTMLButtonElement;
    this.pauseBtn = this.root.querySelector('.touch-pause') as HTMLButtonElement;

    this.onPointerDown = (e) => this.handlePointerDown(e);
    this.onPointerMove = (e) => this.handlePointerMove(e);
    this.onPointerUp = (e) => this.handlePointerUp(e);
    this.onContextMenu = (e) => e.preventDefault();
    this.onVisibility = () => {
      if (document.hidden) this.resetAll();
    };

    const surfaces: HTMLElement[] = [
      this.lookLayer,
      this.stickBase,
      this.jumpBtn,
      this.forgeBtn,
      this.shopBtn,
      this.pauseBtn,
    ];
    for (const el of surfaces) {
      el.addEventListener('pointerdown', this.onPointerDown);
      // Captured pointermoves are delivered to the capture target — listen here,
      // not only on window, or stick/look freezes after the first sample.
      el.addEventListener('pointermove', this.onPointerMove);
      el.addEventListener('pointerup', this.onPointerUp);
      el.addEventListener('pointercancel', this.onPointerUp);
      el.addEventListener('lostpointercapture', this.onPointerUp);
    }
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
    this.root.addEventListener('contextmenu', this.onContextMenu);
    document.addEventListener('visibilitychange', this.onVisibility);

    this.injectStyles();

    if (prefersTouchUi()) {
      this.setEnabled(true);
    } else {
      // Activate on first real touch anywhere in the app.
      const activate = (e: PointerEvent): void => {
        if (e.pointerType === 'touch') {
          this.setEnabled(true);
          window.removeEventListener('pointerdown', activate, true);
        }
      };
      window.addEventListener('pointerdown', activate, true);
    }
  }

  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    this.root.classList.toggle('active', enabled);
    this.parent.classList.toggle('touch-mode', enabled);
    if (enabled) {
      this.input.setUsingTouch(true);
    } else {
      this.resetAll();
      this.visible = false;
      this.root.classList.remove('visible');
      this.parent.classList.remove('touch-mode');
    }
  }

  /** Hide during menus/pause; keep touch-mode styling (no crosshair). */
  setVisible(visible: boolean): void {
    const next = visible && this.enabled;
    if (this.visible === next) return;
    this.visible = next;
    this.root.classList.toggle('visible', this.visible);
    if (!this.visible) this.resetAll();
  }

  /** Show the Shop button only while standing in a shop voxel. */
  setShopAvailable(available: boolean): void {
    if (this.shopAvailable === available) return;
    this.shopAvailable = available;
    this.shopBtn.hidden = !available;
    this.root.classList.toggle('shop-available', available);
  }

  dispose(): void {
    this.resetAll();
    const surfaces: HTMLElement[] = [
      this.lookLayer,
      this.stickBase,
      this.jumpBtn,
      this.forgeBtn,
      this.shopBtn,
      this.pauseBtn,
    ];
    for (const el of surfaces) {
      el.removeEventListener('pointerdown', this.onPointerDown);
      el.removeEventListener('pointermove', this.onPointerMove);
      el.removeEventListener('pointerup', this.onPointerUp);
      el.removeEventListener('pointercancel', this.onPointerUp);
      el.removeEventListener('lostpointercapture', this.onPointerUp);
    }
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.root.remove();
    this.parent.classList.remove('touch-mode');
  }

  private isTouchPointer(e: PointerEvent): boolean {
    // Coarse-pointer devices sometimes synthesize mouse events in DevTools /
    // hybrid tablets — accept those once touch UI is already enabled.
    return e.pointerType === 'touch' || e.pointerType === 'pen' || this.enabled;
  }

  private clearHoldPrimaryTimer(): void {
    if (this.holdPrimaryTimer !== null) {
      clearTimeout(this.holdPrimaryTimer);
      this.holdPrimaryTimer = null;
    }
  }

  private handlePointerDown(e: PointerEvent): void {
    if (!this.visible || !this.isTouchPointer(e)) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const target = e.currentTarget as HTMLElement;

    if (target === this.pauseBtn) {
      e.preventDefault();
      e.stopPropagation();
      if (this.options.onPause) this.options.onPause();
      else this.input.pulseTouchAction('pause');
      return;
    }

    if (target === this.forgeBtn) {
      e.preventDefault();
      e.stopPropagation();
      // Open immediately on tap — do not require hold, and avoid look-pad mining.
      if (this.options.onForge) this.options.onForge();
      else this.input.pulseTouchAction('inventory');
      return;
    }

    if (target === this.shopBtn) {
      e.preventDefault();
      e.stopPropagation();
      if (!this.shopAvailable) return;
      if (this.options.onShop) this.options.onShop();
      else this.input.pulseTouchAction('interact');
      return;
    }

    if (target === this.jumpBtn) {
      e.preventDefault();
      e.stopPropagation();
      this.jumpPointerId = e.pointerId;
      this.jumpBtn.setPointerCapture(e.pointerId);
      this.input.setTouchAction('jump', true);
      return;
    }

    if (target === this.stickBase || target.closest?.('.touch-stick') === this.stickBase) {
      if (this.movePointerId !== null) return;
      e.preventDefault();
      e.stopPropagation();
      this.movePointerId = e.pointerId;
      const rect = this.stickBase.getBoundingClientRect();
      this.stickOriginX = rect.left + rect.width / 2;
      this.stickOriginY = rect.top + rect.height / 2;
      this.stickRadius = Math.max(24, rect.width / 2);
      this.stickBase.setPointerCapture(e.pointerId);
      this.updateStick(e.clientX, e.clientY);
      return;
    }

    if (target === this.lookLayer) {
      if (this.lookPointerId !== null) return;
      e.preventDefault();
      this.lookPointerId = e.pointerId;
      this.lookStartX = e.clientX;
      this.lookStartY = e.clientY;
      this.lastLookX = e.clientX;
      this.lastLookY = e.clientY;
      this.lookDragging = false;
      this.primaryHeldFromTouch = false;
      this.clearHoldPrimaryTimer();
      this.holdPrimaryTimer = setTimeout(() => {
        this.holdPrimaryTimer = null;
        if (this.lookPointerId !== e.pointerId || this.lookDragging) return;
        this.primaryHeldFromTouch = true;
        this.input.setTouchAimPoint(this.lookStartX, this.lookStartY);
        this.input.setTouchAction('primary', true);
      }, HOLD_PRIMARY_MS);
      this.lookLayer.setPointerCapture(e.pointerId);
      this.lookLayer.classList.add('active');
    }
  }

  private handlePointerMove(e: PointerEvent): void {
    if (!this.visible) return;

    if (e.pointerId === this.movePointerId) {
      e.preventDefault();
      this.updateStick(e.clientX, e.clientY);
      return;
    }

    if (e.pointerId === this.lookPointerId) {
      e.preventDefault();
      if (!this.lookDragging) {
        const dist = Math.hypot(e.clientX - this.lookStartX, e.clientY - this.lookStartY);
        if (dist < LOOK_SLOP_PX) return;
        // Crossed into look-drag: cancel tap/hold primary so looking does not fire.
        this.lookDragging = true;
        this.clearHoldPrimaryTimer();
        if (this.primaryHeldFromTouch) {
          this.primaryHeldFromTouch = false;
          this.input.setTouchAction('primary', false);
        }
        this.input.clearTouchAimPoint();
        this.lastLookX = e.clientX;
        this.lastLookY = e.clientY;
        return;
      }
      const dx = (e.clientX - this.lastLookX) * LOOK_DRAG_SCALE;
      const dy = (e.clientY - this.lastLookY) * LOOK_DRAG_SCALE;
      this.lastLookX = e.clientX;
      this.lastLookY = e.clientY;
      this.input.addTouchLook(dx, dy);
    }
  }

  private handlePointerUp(e: PointerEvent): void {
    if (e.pointerId === this.movePointerId) {
      this.movePointerId = null;
      this.input.setTouchMovement(0, 0);
      this.stickKnob.style.transform = 'translate(-50%, -50%)';
      return;
    }

    if (e.pointerId === this.lookPointerId) {
      this.clearHoldPrimaryTimer();
      const wasDragging = this.lookDragging;
      const wasHolding = this.primaryHeldFromTouch;
      this.lookPointerId = null;
      this.lookDragging = false;
      this.primaryHeldFromTouch = false;
      this.lookLayer.classList.remove('active');
      if (wasHolding) {
        this.input.setTouchAction('primary', false);
        this.input.clearTouchAimPoint();
      } else if (!wasDragging) {
        // Tap at this screen point → attack / mine along that ray.
        this.input.setTouchAimPoint(this.lookStartX, this.lookStartY);
        this.input.pulseTouchAction('primary');
      }
      return;
    }

    if (e.pointerId === this.jumpPointerId) {
      this.jumpPointerId = null;
      this.input.setTouchAction('jump', false);
    }
  }

  private updateStick(clientX: number, clientY: number): void {
    let dx = clientX - this.stickOriginX;
    let dy = clientY - this.stickOriginY;
    const len = Math.hypot(dx, dy);
    const max = this.stickRadius;
    if (len > max && len > 0) {
      dx = (dx / len) * max;
      dy = (dy / len) * max;
    }
    this.stickKnob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;

    let strafe = dx / max;
    let forward = -dy / max;
    const mag = Math.hypot(strafe, forward);
    if (mag < JOYSTICK_DEADZONE) {
      this.input.setTouchMovement(0, 0);
      return;
    }
    const norm = (mag - JOYSTICK_DEADZONE) / (1 - JOYSTICK_DEADZONE);
    const scale = norm / mag;
    this.input.setTouchMovement(forward * scale, strafe * scale);
  }

  private resetAll(): void {
    this.clearHoldPrimaryTimer();
    this.movePointerId = null;
    this.lookPointerId = null;
    this.jumpPointerId = null;
    this.lookDragging = false;
    this.primaryHeldFromTouch = false;
    this.input.setTouchMovement(0, 0);
    this.input.setTouchAction('primary', false);
    this.input.setTouchAction('jump', false);
    this.input.setTouchAction('pause', false);
    this.input.setTouchAction('inventory', false);
    this.input.setTouchAction('interact', false);
    this.input.clearTouchAimPoint();
    this.stickKnob.style.transform = 'translate(-50%, -50%)';
    this.lookLayer.classList.remove('active');
  }

  private injectStyles(): void {
    if (document.getElementById('touch-controls-styles')) return;
    const style = document.createElement('style');
    style.id = 'touch-controls-styles';
    style.textContent = `
      #touch-controls {
        position: absolute;
        inset: 0;
        z-index: 10;
        pointer-events: none;
        display: none;
        touch-action: none;
        user-select: none;
        -webkit-user-select: none;
      }
      #touch-controls.active.visible {
        display: block;
      }
      /* Look pad leaves a top chrome strip free so Forge/Pause get clean taps. */
      #touch-controls .touch-look {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        top: 58px;
        pointer-events: auto;
        touch-action: none;
      }
      #touch-controls .touch-look.active {
        background: transparent;
      }
      #touch-controls .touch-stick {
        position: absolute;
        left: max(10px, env(safe-area-inset-left));
        bottom: max(14px, env(safe-area-inset-bottom));
        width: ${JOYSTICK_RADIUS * 2}px;
        height: ${JOYSTICK_RADIUS * 2}px;
        border-radius: 50%;
        border: 2px solid rgba(232, 238, 245, 0.35);
        background: rgba(8, 12, 18, 0.4);
        pointer-events: auto;
        touch-action: none;
        z-index: 2;
      }
      #touch-controls .touch-stick-knob {
        position: absolute;
        left: 50%;
        top: 50%;
        width: 44%;
        height: 44%;
        margin: 0;
        border-radius: 50%;
        border: 2px solid rgba(232, 238, 245, 0.55);
        background: rgba(232, 238, 245, 0.22);
        transform: translate(-50%, -50%);
        pointer-events: none;
      }
      #touch-controls .touch-btn {
        position: absolute;
        pointer-events: auto;
        touch-action: manipulation;
        z-index: 5;
        border: 2px solid rgba(232, 238, 245, 0.35);
        background: rgba(8, 12, 18, 0.55);
        color: rgba(232, 238, 245, 0.9);
        font-family: ui-rounded, "Trebuchet MS", "Segoe UI", sans-serif;
        font-size: 11px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        border-radius: 50%;
        -webkit-tap-highlight-color: transparent;
        -webkit-user-select: none;
        user-select: none;
      }
      #touch-controls .touch-btn:active {
        background: rgba(232, 238, 245, 0.2);
        border-color: rgba(232, 238, 245, 0.7);
      }
      #touch-controls .touch-jump {
        right: max(12px, env(safe-area-inset-right));
        bottom: max(16px, env(safe-area-inset-bottom));
        width: 64px;
        height: 64px;
      }
      /* Sit left of the (shrunk) minimap so pause stays tappable and visible. */
      #touch-controls .touch-pause {
        right: max(100px, calc(env(safe-area-inset-right) + 88px));
        top: max(10px, env(safe-area-inset-top));
        width: 44px;
        height: 44px;
        font-size: 10px;
        letter-spacing: 0.12em;
      }
      /* Left of pause — opens Forge on a single tap (same as Tab / gamepad Y). */
      #touch-controls .touch-forge {
        right: max(152px, calc(env(safe-area-inset-right) + 140px));
        top: max(8px, env(safe-area-inset-top));
        width: 56px;
        height: 56px;
        font-size: 9px;
        letter-spacing: 0.02em;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1px;
        border-color: rgba(255, 179, 71, 0.55);
        background: rgba(40, 24, 8, 0.65);
        color: #ffb347;
      }
      #touch-controls .touch-forge-icon {
        width: 22px;
        height: 22px;
        display: block;
        line-height: 0;
      }
      #touch-controls .touch-forge-icon svg {
        width: 100%;
        height: 100%;
        display: block;
      }
      #touch-controls .touch-forge-label {
        font-size: 8px;
        font-weight: 700;
        line-height: 1;
      }
      /* Left of forge — only while standing in a shop voxel. */
      #touch-controls .touch-shop {
        right: max(216px, calc(env(safe-area-inset-right) + 204px));
        top: max(8px, env(safe-area-inset-top));
        width: 56px;
        height: 56px;
        font-size: 9px;
        letter-spacing: 0.02em;
        display: none;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1px;
        border-color: rgba(240, 196, 58, 0.6);
        background: rgba(36, 28, 8, 0.7);
        color: #f0c43a;
      }
      #touch-controls.shop-available .touch-shop {
        display: flex;
      }
      #touch-controls .touch-shop[hidden] {
        display: none !important;
      }
      #touch-controls .touch-shop-icon {
        width: 22px;
        height: 22px;
        display: block;
        line-height: 0;
      }
      #touch-controls .touch-shop-icon svg {
        width: 100%;
        height: 100%;
        display: block;
      }
      #touch-controls .touch-shop-label {
        font-size: 8px;
        font-weight: 700;
        line-height: 1;
      }
      #app.touch-mode #crosshair {
        display: none;
      }
      #app.touch-mode #hp-panel {
        top: max(8px, env(safe-area-inset-top));
        min-width: 120px;
        max-width: min(200px, calc(100vw - 310px));
        font-size: 10px;
      }
      #app.touch-mode #hp-panel .hp-row {
        margin-bottom: 4px;
      }
      #app.touch-mode #hp-panel .hp-track {
        height: 7px;
      }
      /* Bottom-center gutter between stick and jump — stays out of the play view. */
      #app.touch-mode #hotbar {
        bottom: max(12px, env(safe-area-inset-bottom));
        left: 50%;
        pointer-events: auto;
        z-index: 11;
        gap: 3px;
        max-width: min(420px, calc(100vw - 200px));
        transform: translateX(-50%) scale(0.82);
        transform-origin: bottom center;
      }
      #app.touch-mode #hotbar .hot-slot {
        cursor: pointer;
        min-width: 0;
        flex: 1 1 0;
        width: 52px;
        max-width: 64px;
        height: 44px;
        padding: 4px;
        font-size: 9px;
        pointer-events: auto;
        overflow: hidden;
      }
      #app.touch-mode #hotbar .hot-slot .key {
        display: none;
      }
      #app.touch-mode #hotbar .hot-slot .item-icon {
        width: 28px !important;
        height: 28px !important;
      }
      #app.touch-mode #hud {
        left: max(8px, env(safe-area-inset-left));
        top: max(8px, env(safe-area-inset-top));
        font-size: 10px;
        line-height: 1.3;
        max-width: min(36vw, 160px);
        overflow: hidden;
      }
      #app.touch-mode #minimap {
        width: 84px;
        height: 84px;
        top: max(8px, env(safe-area-inset-top));
        right: max(8px, env(safe-area-inset-right));
      }

      /* Short landscape phones: shrink chrome so the cave stays visible. */
      @media (max-height: 500px) {
        #touch-controls .touch-stick {
          width: 84px;
          height: 84px;
          bottom: max(8px, env(safe-area-inset-bottom));
          left: max(8px, env(safe-area-inset-left));
        }
        #touch-controls .touch-jump {
          width: 52px;
          height: 52px;
          bottom: max(10px, env(safe-area-inset-bottom));
          right: max(10px, env(safe-area-inset-right));
          font-size: 9px;
        }
        #touch-controls .touch-pause {
          width: 36px;
          height: 36px;
          right: max(78px, calc(env(safe-area-inset-right) + 70px));
          top: max(6px, env(safe-area-inset-top));
        }
        #touch-controls .touch-forge {
          width: 44px;
          height: 44px;
          right: max(120px, calc(env(safe-area-inset-right) + 112px));
          top: max(4px, env(safe-area-inset-top));
        }
        #touch-controls .touch-forge-icon {
          width: 18px;
          height: 18px;
        }
        #touch-controls .touch-shop {
          width: 44px;
          height: 44px;
          right: max(168px, calc(env(safe-area-inset-right) + 160px));
          top: max(4px, env(safe-area-inset-top));
        }
        #touch-controls .touch-shop-icon {
          width: 18px;
          height: 18px;
        }
        #touch-controls .touch-look {
          top: 50px;
        }
        #app.touch-mode #hotbar {
          bottom: max(8px, env(safe-area-inset-bottom));
          max-width: min(360px, calc(100vw - 170px));
          transform: translateX(-50%) scale(0.72);
        }
        #app.touch-mode #hotbar .hot-slot {
          height: 34px;
          max-width: 56px;
          padding: 3px 4px;
        }
        #app.touch-mode #hud {
          font-size: 9px;
          max-width: min(30vw, 130px);
          line-height: 1.25;
        }
        #app.touch-mode #hp-panel {
          max-width: min(160px, calc(100vw - 290px));
          font-size: 9px;
        }
        #app.touch-mode #minimap {
          width: 64px;
          height: 64px;
          top: max(6px, env(safe-area-inset-top));
          right: max(6px, env(safe-area-inset-right));
        }
      }

      /* Very narrow widths: tuck hotbar a bit higher so stick/jump keep their corners. */
      @media (max-width: 700px) and (min-height: 501px) {
        #app.touch-mode #hotbar {
          bottom: max(96px, calc(env(safe-area-inset-bottom) + 88px));
          max-width: calc(100vw - 24px);
          transform: translateX(-50%) scale(0.9);
        }
      }

      /* Tablets / large touch screens: roomier stick + buttons. */
      @media (min-width: 768px) and (min-height: 501px) {
        #touch-controls .touch-stick {
          width: 148px;
          height: 148px;
          left: max(18px, env(safe-area-inset-left));
          bottom: max(22px, env(safe-area-inset-bottom));
        }
        #touch-controls .touch-jump {
          width: 88px;
          height: 88px;
          right: max(20px, env(safe-area-inset-right));
          bottom: max(24px, env(safe-area-inset-bottom));
          font-size: 13px;
        }
        #touch-controls .touch-pause {
          width: 52px;
          height: 52px;
          right: max(122px, calc(env(safe-area-inset-right) + 108px));
          top: max(12px, env(safe-area-inset-top));
          font-size: 12px;
        }
        #touch-controls .touch-forge {
          width: 64px;
          height: 64px;
          right: max(184px, calc(env(safe-area-inset-right) + 170px));
          top: max(10px, env(safe-area-inset-top));
        }
        #touch-controls .touch-forge-icon {
          width: 26px;
          height: 26px;
        }
        #touch-controls .touch-forge-label {
          font-size: 10px;
        }
        #touch-controls .touch-shop {
          width: 64px;
          height: 64px;
          right: max(256px, calc(env(safe-area-inset-right) + 242px));
          top: max(10px, env(safe-area-inset-top));
        }
        #touch-controls .touch-shop-icon {
          width: 26px;
          height: 26px;
        }
        #touch-controls .touch-shop-label {
          font-size: 10px;
        }
        #touch-controls .touch-look {
          top: 72px;
        }
        #app.touch-mode #hotbar {
          max-width: min(520px, calc(100vw - 280px));
          transform: translateX(-50%) scale(1);
          gap: 5px;
        }
        #app.touch-mode #hotbar .hot-slot {
          height: 48px;
          max-width: 72px;
          padding: 5px 6px;
          font-size: 10px;
        }
        #app.touch-mode #hotbar .hot-slot .item-icon {
          width: 32px !important;
          height: 32px !important;
        }
        #app.touch-mode #minimap {
          width: 100px;
          height: 100px;
        }
        #app.touch-mode #hp-panel {
          min-width: 140px;
          max-width: min(240px, calc(100vw - 360px));
          font-size: 11px;
        }
        #app.touch-mode #hud {
          font-size: 11px;
          max-width: min(32vw, 200px);
        }
      }
    `;
    document.head.appendChild(style);
  }
}
